// Wire input: keyboard listeners + global key state, DOM events for non-pane
// widgets, selection-cube pointer drag and hover, and the window-resize hook
// that keeps camera aspect + renderer size in sync.

import * as THREE from 'three';
import { params, store } from '@store/instance';
import { physicalToThreeJS, threeJSToPhysical } from '@adapters/coordinates';
import {
    initSelectionInteractions,
    setupSelectionHoverCursor,
} from '@scene/picking';
import {
    updateSelectedRegion,
    updateSelectionVisuals,
} from '@scene/selection';
import { buildRegionContext } from '@scene/regionContext';
import { setupListeners as setupKeyboardListeners } from '@input/keyboard';
import { setupListeners as setupDOMEventListeners } from '@ui/events';
import { applyCameraViewport } from '../cameraProjection';
import { flyApplyLook, flyBeginLook, initNavMode } from '../cameraModes';
import { INTENTS } from '@store/intents';
import type { ViewerContext } from '../context';

/** Fly-mode look control — drag to look, with pointer lock as a progressive
 *  enhancement. On click we request pointer lock (best-effort, from the gesture):
 *  if the browser grants it the cursor hides and bare movement looks until Esc;
 *  if it denies it (focus/cooldown, sandboxed frame, headless) the held-drag
 *  path still yaws/pitches the camera, so a click always does something. Inert
 *  outside fly mode (OrbitControls owns orbit/turntable); a press on the
 *  selection marker is left to the selection-drag handler.
 *
 *  Exit: N cycles out of fly (shortcut registry); Esc — once the cursor is no
 *  longer captured — switches back to orbit. Returns a disposer. */
function installFlyLook(viewer: ViewerContext): (() => void) {
    const renderer = viewer.three.renderer;
    if (!renderer) return () => { /* no renderer */ };
    const dom = renderer.domElement;
    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    let dragging = false;

    const isLocked = (): boolean => typeof document !== 'undefined' && document.pointerLockElement === dom;

    const hitsSelection = (clientX: number, clientY: number): boolean => {
        const camera = viewer.three.camera;
        const inv = viewer.objects.selection.invisibleMesh;
        if (!camera || !inv) return false;
        const rect = dom.getBoundingClientRect();
        ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
        raycaster.setFromCamera(ndc, camera);
        return raycaster.intersectObject(inv, false).length > 0;
    };

    const onPointerDown = (e: PointerEvent): void => {
        if (params.navMode !== 'fly' || e.button !== 0) return;
        if (hitsSelection(e.clientX, e.clientY)) return; // selection drag wins
        flyBeginLook(viewer); // absorb any camera change since the last look session
        dragging = true;
        // Track the drag past the canvas edge so look doesn't stall mid-gesture.
        // (Released when lock engages — capture is irrelevant with a hidden cursor.)
        try { dom.setPointerCapture(e.pointerId); } catch { /* unsupported */ }
        // requestPointerLock must be called from a user gesture (this one); the
        // returned promise rejects silently when denied — the drag path covers it.
        if (!isLocked()) { try { void dom.requestPointerLock(); } catch { /* denied */ } }
    };
    const onPointerMove = (e: PointerEvent): void => {
        if (params.navMode === 'fly' && (isLocked() || dragging)) flyApplyLook(viewer, e.movementX, e.movementY);
    };
    const onPointerUp = (e: PointerEvent): void => {
        if (!dragging) return;
        dragging = false; // lock (if granted) keeps look live cursor-free until Esc
        try { dom.releasePointerCapture(e.pointerId); } catch { /* not captured */ }
    };

    // Esc exits fly → orbit, but only once the cursor is free: while captured the
    // browser's first Esc just releases pointer lock (we no-op), so it's a natural
    // two-step. N exits in one press regardless (shortcut registry / cycle).
    const onKeyDown = (e: KeyboardEvent): void => {
        if (e.key !== 'Escape' || params.navMode !== 'fly' || isLocked()) return;
        store.dispatch(INTENTS.CAMERA_SET_NAV_MODE, { mode: 'orbit' });
    };

    // Leaving fly mode while pointer-locked → release the cursor so the user
    // isn't stranded with a hidden pointer in orbit/turntable.
    const offNav = store.subscribe('params.navMode', () => {
        if (params.navMode !== 'fly' && isLocked()) document.exitPointerLock?.();
    });

    dom.addEventListener('pointerdown', onPointerDown);
    dom.addEventListener('pointermove', onPointerMove);
    dom.addEventListener('pointerup', onPointerUp);
    dom.addEventListener('pointercancel', onPointerUp);
    window.addEventListener('keydown', onKeyDown);
    return () => {
        dom.removeEventListener('pointerdown', onPointerDown);
        dom.removeEventListener('pointermove', onPointerMove);
        dom.removeEventListener('pointerup', onPointerUp);
        dom.removeEventListener('pointercancel', onPointerUp);
        window.removeEventListener('keydown', onKeyDown);
        offNav();
        if (isLocked()) document.exitPointerLock?.();
    };
}

/** (Re)build the selection pointer interactions against the *current*
 *  viewer.three.camera/controls. Returns the disposer. Called at boot and again
 *  after a camera projection swap (which recreates camera + controls). */
export function reinitSelectionInteractions(viewer: ViewerContext): (() => void) {
    const camera = viewer.three.camera;
    const renderer = viewer.three.renderer;
    if (!camera || !renderer) return () => { /* three stack not ready */ };
    return initSelectionInteractions({
        THREE,
        renderer,
        camera,
        controls: viewer.three.controls,
        cubeCenterDot: viewer.objects.selection.centerDot!,
        invisibleCubeMesh: viewer.objects.selection.invisibleMesh!,
        physicalToThreeJS,
        threeJSToPhysical,
        getCoordUnits: () => (viewer.sceneMeta.units || ''),
        getFieldUnits: () => ((viewer.field.metadata && viewer.field.metadata.field_units) ? viewer.field.metadata.field_units : ''),
        getFieldLabel: () => (viewer.field.currentComponent || 'Field'),
    });
}

export function wireInput(viewer: ViewerContext): void {
    const camera = viewer.three.camera;
    const renderer = viewer.three.renderer;
    if (!camera || !renderer) return;

    viewer.dispose.selectionInteractions = reinitSelectionInteractions(viewer);
    viewer.dispose.flyLook = installFlyLook(viewer);

    // Adopt the persisted navigation mode (runs the controller's enter()).
    initNavMode(viewer);

    void updateSelectionVisuals;

    setupKeyboardListeners(viewer);
    viewer.dispose.domEvents = setupDOMEventListeners(viewer);
    setupSelectionHoverCursor(viewer);

    const resizeHandler = (): void => onWindowResize(viewer);
    window.addEventListener('resize', resizeHandler);
    onWindowResize(viewer);

    if (viewer.objects.selection.invisibleMesh) {
        updateSelectedRegion(buildRegionContext(viewer, params), viewer.objects.selection.invisibleMesh.position);
    }
}

export function onWindowResize(viewer: ViewerContext): void {
    if (viewer.loop.handle) { viewer.loop.handle.onResize(); return; }
    const camera = viewer.three.camera;
    if (camera) applyCameraViewport(camera);
    if (viewer.three.renderer) viewer.three.renderer.setSize(window.innerWidth, window.innerHeight);
}
